import { useCallback, useMemo, useRef, useState, type FC } from "react";
import type { EditorRef } from "react-email-editor";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import SaveDraftModal from "src/layouts/Engagements/Campaigns/shared/SaveDraftModal";
import CampaignFlowActions from "src/layouts/Engagements/Campaigns/shared/CampaignFlowActions";
import CampaignNextButton from "src/layouts/Engagements/Campaigns/shared/CampaignNextButton";
import { ShowFieldErrorsContext } from "src/layouts/Engagements/Campaigns/shared/UseRequiredFieldError";
import EmailStatusPopup from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/EmailStatusPopup";
import ChatScheduleStep from "./ChatScheduleStep";
import AiGeneratingCampaignLoader from "./AiGeneratingCampaignLoader";
import CampaignStepCard, { CampaignStepCardChromeContext } from "src/layouts/Engagements/Campaigns/shared/CampaignStepCard";
import ProductCategoriesStep from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/steps/ProductCategoriesStep";
import CampaignCategoryStep from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/steps/CampaignCategoryStep";
import CampaignSubCategoryStep from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/steps/CampaignSubCategoryStep";
import AiPromptStep from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/steps/AiPromptStep";
import ReviewDetailsStep from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/steps/ReviewDetailsStep";
import SenderRecipientsStep from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/steps/SenderRecipientsStep";
import CreateCampaignStep from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/steps/CreateCampaignStep";
import { useProductCategories } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/hooks/UseProductCategories";
import { useChatDraft } from "./UseChatDraft";
import { useCampaignDelivery } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/hooks/UseCampaignDelivery";
import { useRecommendedProducts } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/hooks/UseRecommendedProducts";
import { EMAIL_WIZARD_STEPS, MAX_PRODUCT_CATEGORIES } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignConstants";
import { saveEditorDesign } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/EmailEditorUtils";
import type { AiCampaignStepKey } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignTypes";
import {
  FIRST_WIZARD_STEP_INDEX,
  type ChatWizardStepSnapshot,
  type useChatEmailCampaignWizard,
} from "./UseChatEmailCampaignWizard";
import "src/layouts/Engagements/Campaigns/shared/CampaignFlow.scss";
import "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignWizard.scss";
import "./ChatCampaignWizard.scss";

const WIZARD_KEY = "campaignFlow.aiWizard";

const noop = () => undefined;

const CREATE_STEP_INDEX = EMAIL_WIZARD_STEPS.findIndex(step => step.key === "createCampaign");
const SELECTION_STEP_KEYS: AiCampaignStepKey[] = [
  "productCategories",
  "campaignCategory",
  "campaignSubCategory",
];

export type { ChatWizardStepSnapshot as FrozenWizardSnapshot } from "./UseChatEmailCampaignWizard";

interface ChatCampaignWizardProps {
  wizard: ReturnType<typeof useChatEmailCampaignWizard>;
  onBackToSetup: () => void;
  frozenSnapshot?: ChatWizardStepSnapshot;
  onDraftSaved?: () => void;
}

const ChatCampaignWizard: FC<ChatCampaignWizardProps> = ({
  wizard: aiWizard,
  onBackToSetup,
  frozenSnapshot,
  onDraftSaved,
}) => {
  const { t, i18n } = useTranslation();
  const editorRef = useRef<EditorRef>(null);
  const frozen = Boolean(frozenSnapshot);

  const stepIndex = frozenSnapshot?.stepIndex ?? aiWizard.stepIndex;
  const stepKey = frozenSnapshot?.stepKey ?? aiWizard.stepKey;
  const form = frozenSnapshot?.form ?? aiWizard.form;
  const {
    template,
    senderDetails,
    sendTo,
    excludeList,
    audienceOptions,
    isListsLoading,
    canProceed,
    goNext,
    goBack,
    editStep,
    setReplyTo,
    setSendTo,
    subjectSuggestions,
    saveTemplateDesign,
    draft,
  } = aiWizard;
  const isGenerating = !frozen && aiWizard.isGenerating;
  const updateForm = frozen ? noop : aiWizard.updateForm;

  const handleCategoriesChange = useCallback(
    (categories: string[]) => updateForm("selectedCategories", categories),
    [updateForm]
  );
  const categoriesState = useProductCategories(
    form.selectedCategories,
    handleCategoriesChange,
    !frozen,
    MAX_PRODUCT_CATEGORIES
  );

  const {
    isSending,
    isScheduleOpen,
    deliveryStatus,
    sendNow,
    schedule,
    openSchedule,
    closeSchedule,
    clearDeliveryStatus,
  } = useCampaignDelivery({
    editorRef,
    template,
    campaignName: form.campaignName,
    subjectLine: form.subjectLine,
    senderDetails,
    replyTo: senderDetails.replyTo,
    draftId: draft.draftId,
    onDelivered: draft.close,
  });

  const [errorStepKey, setErrorStepKey] = useState<AiCampaignStepKey | null>(null);
  const revealStepErrors = useCallback(() => {
    setErrorStepKey(stepKey);
    if (SELECTION_STEP_KEYS.includes(stepKey)) {
      goNext();
    }
  }, [goNext, stepKey]);

  const isCreateStep = stepKey === "createCampaign";
  const recommendedProducts = useRecommendedProducts({
    audienceNames: senderDetails.recipients,
    excludeAudienceNames: senderDetails.exclusions,
    campaignName: form.campaignName,
    enabled: isCreateStep && !frozen,
  });

  const stepCountLabel = useMemo(
    () =>
      t("wizardStepper.stepOf", {
        step: formatNumber((isGenerating ? CREATE_STEP_INDEX : stepIndex) + 1, {}, i18n.language),
        total: formatNumber(EMAIL_WIZARD_STEPS.length, {}, i18n.language),
      }),
    [i18n.language, isGenerating, stepIndex, t]
  );
  const isBusy = isGenerating || isSending;

  const { save: saveWizardDraft } = draft;
  const saveDraft = useCallback(async () => {
    const design = isCreateStep ? await saveEditorDesign(editorRef.current?.editor) : null;
    if (design) {
      saveTemplateDesign(design);
    }
    return saveWizardDraft(design);
  }, [isCreateStep, saveTemplateDesign, saveWizardDraft]);
  const { isDraftOpen, openDraft, closeDraft, handleSaveDraft } = useChatDraft(saveDraft, onDraftSaved);

  const handleBack = useCallback(async () => {
    if (isCreateStep) {
      const design = await saveEditorDesign(editorRef.current?.editor);
      if (design) {
        saveTemplateDesign(design);
      }
    }
    if (stepIndex === FIRST_WIZARD_STEP_INDEX) {
      onBackToSetup();
      return;
    }
    goBack();
  }, [goBack, isCreateStep, onBackToSetup, saveTemplateDesign, stepIndex]);

  const renderAiWizardStep = () => {
    switch (stepKey) {
      case "productCategories":
        return (
          <ProductCategoriesStep
            selectedCategories={form.selectedCategories}
            categoriesState={categoriesState}
            maxSelection={MAX_PRODUCT_CATEGORIES}
          />
        );
      case "campaignCategory":
        return <CampaignCategoryStep form={form} updateForm={updateForm} />;
      case "campaignSubCategory":
        return <CampaignSubCategoryStep form={form} updateForm={updateForm} />;
      case "aiPrompt":
        return <AiPromptStep form={form} updateForm={updateForm} />;
      case "senderRecipients":
        return (
          <SenderRecipientsStep
            subjectLine={form.subjectLine}
            onSubjectLineChange={value => updateForm("subjectLine", value)}
            subjectSuggestions={subjectSuggestions}
            senderDetails={senderDetails}
            sendTo={sendTo}
            audienceOptions={audienceOptions}
            isListsLoading={isListsLoading}
            onReplyToChange={setReplyTo}
            onSendToChange={setSendTo}
          />
        );
      case "reviewDetails":
        if (isGenerating) {
          return (
            <CampaignStepCard title={form.campaignName || t(`${WIZARD_KEY}.details.title`)}>
              <AiGeneratingCampaignLoader />
            </CampaignStepCard>
          );
        }
        return (
          <ReviewDetailsStep
            form={form}
            sendTo={sendTo}
            excludeList={excludeList}
            audienceOptions={audienceOptions}
            senderDetails={senderDetails}
            onEdit={key => (key === "campaignDetails" ? onBackToSetup() : editStep(key))}
          />
        );
      case "createCampaign":
        return (
          <CreateCampaignStep
            title={t(`${WIZARD_KEY}.createCampaign.title`)}
            subtitle={t(`${WIZARD_KEY}.createCampaign.subtitle`)}
            editorRef={editorRef}
            template={template}
            recommendedProducts={recommendedProducts}
          />
        );
      default:
        return null;
    }
  };

  const footer = frozen || (!isScheduleOpen && !isGenerating) ? (
      <CampaignFlowActions
        onBack={handleBack}
        backDisabled={isBusy}
        wide={isCreateStep}
        note={isCreateStep ? undefined : t(`${WIZARD_KEY}.chatNote`)}
      >
        <button
          type="button"
          className="button button--link campaign-flow__action campaign-flow__action--link"
          onClick={openDraft}
          disabled={isBusy}
        >
          {t("campaignFlow.common.saveDraft")}
        </button>
        {isCreateStep ? (
          <>
            <button
              type="button"
              className="button button--primary campaign-flow__action"
              onClick={openSchedule}
              disabled={isSending}
            >
              {t(`${WIZARD_KEY}.createCampaign.scheduleCampaign`)}
            </button>
            <button
              type="button"
              className="button button--primary campaign-flow__action"
              onClick={sendNow}
              disabled={isSending}
            >
              {isSending
                ? t(`${WIZARD_KEY}.createCampaign.sending`)
                : t(`${WIZARD_KEY}.createCampaign.sendCampaign`)}
            </button>
          </>
        ) : (
          <CampaignNextButton
            canProceed={canProceed}
            onNext={goNext}
            onBlockedClick={revealStepErrors}
          >
            {t(`${WIZARD_KEY}.next`)}
          </CampaignNextButton>
        )}
      </CampaignFlowActions>
  ) : undefined;

  const chrome = { stepLabel: stepCountLabel, footer };

  const frozenClass = frozen ? " chat-campaign-wizard--frozen" : "";

  return (
    <div className={`campaign-flow chat-campaign-wizard${frozenClass}`}>
      {isGenerating && (
        <p className="chat-campaign-wizard__generating-heading">
          {t(`${WIZARD_KEY}.generatingCampaign.heading`)}
        </p>
      )}
      <CampaignStepCardChromeContext.Provider value={chrome}>
        <ShowFieldErrorsContext.Provider value={!frozen && errorStepKey === stepKey}>
          {renderAiWizardStep()}
        </ShowFieldErrorsContext.Provider>
      </CampaignStepCardChromeContext.Provider>

      {!frozen && (
        <SaveDraftModal
          open={isDraftOpen}
          isSaving={draft.isSaving}
          onKeepEditing={closeDraft}
          onSaveAndExit={handleSaveDraft}
        />
      )}

      {!frozen && isScheduleOpen && (
        <ChatScheduleStep isSubmitting={isSending} onCancel={closeSchedule} onSchedule={schedule} />
      )}

      {!frozen && deliveryStatus && (
        <EmailStatusPopup
          type={deliveryStatus.type}
          date={deliveryStatus.date}
          time={deliveryStatus.time}
          onClose={clearDeliveryStatus}
        />
      )}
    </div>
  );
};

export default ChatCampaignWizard;
